// Inventario y control de stock: valorizacion, punto de pedido, cobertura y kardex.
// Recibe todo ya calculado: cargar y agregar es cosa de la pantalla.

import React from 'react';
import { AlertTriangle, Boxes, History, Layers, RefreshCw, Repeat } from 'lucide-react';
import { formatDateLong, formatInt, formatMoney, formatQty, safeName, MOVIMIENTO_TONE } from './formato';
import { DataTable, EmptyState, SectionCard } from './componentes';

export default function TabInventario({
  alertas,
  extraLoading,
  kardexMovimientos,
  kardexProductId,
  kardexProductos,
  kardexQuery,
  puntoPedido,
  range,
  rotacion,
  setKardexProductId,
  setKardexQuery,
  stockValorizado,
}) {
  return (
            <>
              <SectionCard
                title="Stock valorizado"
                icon={Layers}
                action={
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {stockValorizado.rows.length} prod.
                  </span>
                }
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Capital a costo</p>
                    <p className="mt-1 text-xl font-black text-slate-900 tracking-tight">$ {formatMoney(stockValorizado.totalCosto)}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Valor a venta</p>
                    <p className="mt-1 text-xl font-black text-emerald-600 tracking-tight">$ {formatMoney(stockValorizado.totalVenta)}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Unidades en stock</p>
                    <p className="mt-1 text-xl font-black text-slate-900 tracking-tight">{formatInt(stockValorizado.totalUnidades)}</p>
                  </div>
                </div>
                <DataTable
                  emptyMessage="Sin productos con stock"
                  rows={stockValorizado.rows}
                  columns={[
                    { key: 'nombre', label: 'Producto', cellClass: () => 'font-black text-slate-800 truncate max-w-[220px]' },
                    { key: 'stock', label: 'Stock', align: 'right', cellClass: () => 'font-bold text-slate-600', render: (r) => formatInt(r.stock) },
                    { key: 'cost', label: 'Costo', align: 'right', cellClass: () => 'text-slate-500', render: (r) => `$ ${formatMoney(r.cost)}` },
                    { key: 'valorCosto', label: 'Valor costo', align: 'right', cellClass: () => 'font-black text-slate-900', render: (r) => `$ ${formatMoney(r.valorCosto)}` },
                    { key: 'price', label: 'Precio', align: 'right', cellClass: () => 'text-slate-500', render: (r) => `$ ${formatMoney(r.price)}` },
                    { key: 'valorVenta', label: 'Valor venta', align: 'right', cellClass: () => 'font-black text-emerald-600', render: (r) => `$ ${formatMoney(r.valorVenta)}` },
                  ]}
                  footer={
                    <tfoot className="sticky bottom-0 bg-white">
                      <tr className="border-t-2 border-slate-200 text-[11px] font-black text-slate-900">
                        <td className="py-2">Total</td>
                        <td className="py-2 text-right">{formatInt(stockValorizado.totalUnidades)}</td>
                        <td />
                        <td className="py-2 text-right">$ {formatMoney(stockValorizado.totalCosto)}</td>
                        <td />
                        <td className="py-2 text-right text-emerald-600">$ {formatMoney(stockValorizado.totalVenta)}</td>
                      </tr>
                    </tfoot>
                  }
                />
              </SectionCard>

              <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <SectionCard
                  title="Punto de pedido (stock crítico)"
                  icon={AlertTriangle}
                  action={
                    <span className="text-[10px] font-black uppercase tracking-widest text-rose-600">
                      {puntoPedido.length} a reponer
                    </span>
                  }
                >
                  <DataTable
                    emptyMessage="Sin productos bajo el mínimo"
                    rows={puntoPedido}
                    columns={[
                      { key: 'nombre', label: 'Producto', cellClass: () => 'font-black text-slate-800 truncate max-w-[200px]' },
                      { key: 'stock', label: 'Stock', align: 'right', cellClass: () => 'font-bold text-slate-600', render: (r) => formatInt(r.stock) },
                      { key: 'min', label: 'Mínimo', align: 'right', cellClass: () => 'text-slate-500', render: (r) => formatInt(r.min) },
                      { key: 'faltante', label: 'Faltan', align: 'right', cellClass: () => 'font-black text-amber-600', render: (r) => (r.min > 0 ? formatInt(r.faltante) : '-') },
                      {
                        key: 'estado',
                        label: 'Estado',
                        align: 'right',
                        render: (r) => (
                          <span className={`inline-block px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest border ${r.agotado ? 'bg-rose-50 text-rose-700 border-rose-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                            {r.agotado ? 'Agotado' : 'Bajo'}
                          </span>
                        ),
                      },
                    ]}
                  />
                </SectionCard>

                <SectionCard
                  title="Rotación y stock inmovilizado"
                  icon={Repeat}
                  action={
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      {range.days < 7 && 'proyección ruidosa · '}
                      {rotacion.filter((r) => r.sinRotacion).length} inmovilizados
                    </span>
                  }
                >
                  <DataTable
                    emptyMessage="Sin datos de rotación"
                    rows={rotacion}
                    columns={[
                      { key: 'nombre', label: 'Producto', cellClass: () => 'font-black text-slate-800 truncate max-w-[200px]' },
                      { key: 'vendido', label: 'Vendido', align: 'right', cellClass: () => 'font-bold text-slate-600', render: (r) => formatInt(r.vendido) },
                      { key: 'stock', label: 'Stock', align: 'right', cellClass: () => 'text-slate-500', render: (r) => formatInt(r.stock) },
                      { key: 'rotacion', label: 'Rotación', align: 'right', cellClass: () => 'text-slate-500', render: (r) => (r.stock > 0 ? `${r.rotacion.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x` : '-') },
                      {
                        key: 'diasCobertura',
                        label: 'Cobertura',
                        align: 'right',
                        cellClass: (r) =>
                          `font-black ${
                            r.diasCobertura == null
                              ? 'text-slate-400'
                              : r.diasCobertura < 7
                                ? 'text-rose-600'
                                : r.diasCobertura <= 15
                                  ? 'text-amber-600'
                                  : 'text-emerald-600'
                          }`,
                        render: (r) =>
                          r.diasCobertura == null
                            ? '-'
                            : `${Math.round(r.diasCobertura).toLocaleString('es-AR')} días`,
                      },
                      {
                        key: 'estado',
                        label: 'Estado',
                        align: 'right',
                        render: (r) => (
                          <span className={`inline-block px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest border ${r.sinRotacion ? 'bg-slate-100 text-slate-600 border-slate-200' : r.rotacion >= 1 ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>
                            {r.sinRotacion ? 'Inmovilizado' : r.rotacion >= 1 ? 'Alta' : 'Normal'}
                          </span>
                        ),
                      },
                    ]}
                  />
                </SectionCard>
              </section>

              <SectionCard
                title="Kardex · historial de movimientos"
                icon={History}
                action={
                  extraLoading.movimientos && (
                    <RefreshCw size={14} className="text-slate-400 animate-spin" />
                  )
                }
              >
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                  <input
                    type="text"
                    value={kardexQuery}
                    onChange={(e) => setKardexQuery(e.target.value)}
                    placeholder="Buscar producto…"
                    className="flex-1 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none text-slate-700 focus:border-[#51A7FD]"
                  />
                  <select
                    value={kardexProductId}
                    onChange={(e) => setKardexProductId(e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none text-slate-700 focus:border-[#51A7FD]"
                  >
                    <option value="">Elegí un producto…</option>
                    {kardexProductos.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                {!kardexProductId ? (
                  <EmptyState icon={History} message="Elegí un producto para ver su kardex" />
                ) : (
                  <DataTable
                    emptyMessage="Sin movimientos en el período"
                    rows={kardexMovimientos}
                    columns={[
                      { key: 'fecha', label: 'Fecha', cellClass: () => 'text-slate-500', render: (m) => formatDateLong(m.created_at) },
                      {
                        key: 'tipo',
                        label: 'Tipo',
                        render: (m) => (
                          <span className={`inline-block px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest border ${MOVIMIENTO_TONE[m._cls]}`}>
                            {m.tipo || m._cls}
                          </span>
                        ),
                      },
                      {
                        key: 'cantidad',
                        label: 'Cantidad',
                        align: 'right',
                        cellClass: (m) => (m._cls === 'egreso' ? 'font-black text-rose-600' : m._cls === 'ingreso' ? 'font-black text-emerald-600' : 'font-black text-slate-700'),
                        render: (m) => `${m._cls === 'egreso' ? '-' : m._cls === 'ingreso' ? '+' : ''}${formatQty(Math.abs(Number(m.cantidad || 0)))}`,
                      },
                      { key: 'motivo', label: 'Motivo', cellClass: () => 'text-slate-600 truncate max-w-[260px]', render: (m) => m.motivo || '-' },
                      { key: 'sitio', label: 'Sucursal', cellClass: () => 'text-slate-500', render: (m) => m._sitio || '-' },
                    ]}
                  />
                )}
              </SectionCard>

              {/* Atención de stock (migrado del Resumen) */}
              <SectionCard title="Atención de stock" icon={Boxes}>
                {alertas.sinStock.length === 0 && alertas.bajoStock.length === 0 ? (
                  <EmptyState message="Todo el stock está OK" />
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {alertas.sinStock.slice(0, 8).map((p) => (
                      <div key={`sin-${p.id}`} className="flex items-center justify-between bg-rose-50/50 border border-rose-100 rounded-xl px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-black text-slate-800 truncate">{safeName(p.name)}</p>
                          <p className="text-[10px] font-bold text-rose-600 uppercase tracking-widest">Agotado</p>
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-rose-600">0 ud.</span>
                      </div>
                    ))}
                    {alertas.bajoStock.slice(0, 8).map((p) => (
                      <div key={`bajo-${p.id}`} className="flex items-center justify-between bg-amber-50/50 border border-amber-100 rounded-xl px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-black text-slate-800 truncate">{safeName(p.name)}</p>
                          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">Stock bajo</p>
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-600">{p.stock} / {p.min} ud.</span>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </>
  );
}
